"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SectionHeading } from "@/components/ui/section-heading";
import { LogoMark, SvgLogoMark } from "@/components/ui/logo";
import { ease } from "@/lib/motion";
import { cn } from "@/lib/utils";

const MODELS = [
  { id: "p9-fast", name: "Qwen3-8B" },
  { id: "p9-coder", name: "Qwen3-Coder" },
  { id: "p9-reason", name: "gpt-oss 20B" },
  { id: "p9-large", name: "DeepSeek V3.2" },
  { id: "p9-embed", name: "bge-large" },
];
const REQUESTS = [
  { label: "Simple chat", pick: 0 },
  { label: "Code generation", pick: 1 },
  { label: "Deep reasoning", pick: 2 },
  { label: "Long document", pick: 3 },
  { label: "Embeddings", pick: 4 },
];

const ROUTER = { x: 290, y: 230 };
const RX = 46;
const REQ = { x: 90, y: 230 };
// fan of model nodes opening to the right, radius 200 == the request -> router length
const MPT: Array<[number, number]> = [
  [429, 86], [474, 152], [490, 230], [474, 308], [429, 374],
];

/** `numbered` drops the home page's "2." when the section is reused elsewhere (`/developers`). */
export function Router({ numbered = true }: { numbered?: boolean } = {}) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);

  // restarts whenever `active` changes (incl. manual clicks) so the timer resets to 0
  useEffect(() => {
    if (reduce) return;
    const id = setTimeout(() => setActive((v) => (v + 1) % REQUESTS.length), 2600);
    return () => clearTimeout(id);
  }, [reduce, active]);

  const pick = REQUESTS[active].pick;

  return (
    <section id="router" className="relative">
      <div className="mx-auto max-w-[90rem] px-5 py-16 sm:px-8 sm:py-32">
        <SectionHeading
          eyebrow={numbered ? "2. Intelligent model routing" : "Intelligent model routing"}
          title={
            <>
              The <span className="italic">P/9 Router</span> predicts and sends each
              request to the best-fit open-source model, cutting cost while holding
              quality.
            </>
          }
        />

        {/* ---------- desktop: live routing fan ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease }}
          className="mx-auto mt-14 hidden max-w-2xl sm:block"
        >
          <svg
            viewBox="0 0 640 460"
            className="w-full overflow-visible"
            role="img"
            aria-label="The P/9 Router reads each request and routes it to the best-fit model"
          >
            {/* beams router -> models */}
            {MPT.map(([mx, my], i) => {
              const on = i === pick;
              return (
                <line
                  key={`beam-${i}`}
                  x1={ROUTER.x} y1={ROUTER.y} x2={mx} y2={my}
                  stroke="var(--foreground)"
                  strokeWidth={on ? 2 : 1.3}
                  className={on ? undefined : "flow-line"}
                  style={{ opacity: on ? 1 : 0.16, transition: "opacity .45s ease, stroke-width .45s ease" }}
                />
              );
            })}

            {/* request -> router beam (same length as router -> model beams) */}
            <line
              x1={REQ.x} y1={REQ.y} x2={ROUTER.x} y2={ROUTER.y}
              stroke="var(--foreground)" strokeWidth={1.6} className="flow-line"
              style={{ opacity: 0.4 }}
            />

            {/* travelling token */}
            {!reduce && (
              <motion.circle
                key={active}
                r={4} fill="var(--foreground)"
                initial={{ opacity: 0, cx: REQ.x, cy: REQ.y }}
                animate={{
                  cx: [REQ.x, ROUTER.x, MPT[pick][0], MPT[pick][0]],
                  cy: [REQ.y, ROUTER.y, MPT[pick][1], MPT[pick][1]],
                  opacity: [0, 1, 1, 0],
                }}
                transition={{ duration: 2.1, ease: "easeInOut", times: [0, 0.34, 0.7, 1] }}
              />
            )}

            {/* request node, with its label + cycling type grouped right beneath it */}
            <circle cx={REQ.x} cy={REQ.y} r={15} fill="var(--card)" stroke="var(--border-strong)" strokeWidth={1.5} />
            <SvgLogoMark cx={REQ.x} cy={REQ.y} gap={6} r={1.6} color="var(--muted)" diagColor="var(--muted-2)" />
            <text x={REQ.x} y={REQ.y + 32} fill="var(--muted)" fontSize={12} letterSpacing={2} textAnchor="middle" className="font-sans uppercase">
              Request
            </text>
            <AnimatePresence mode="wait">
              <motion.text
                key={active}
                x={REQ.x} fill="var(--foreground)" fontSize={15} textAnchor="middle"
                className="font-serif italic"
                initial={{ opacity: 0, y: REQ.y + 58 }}
                animate={{ opacity: 1, y: REQ.y + 52 }}
                exit={{ opacity: 0, y: REQ.y + 46 }}
                transition={{ duration: 0.4 }}
              >
                {REQUESTS[active].label}
              </motion.text>
            </AnimatePresence>

            {/* router node: its border flashes (to the foreground colour) when the token arrives */}
            <circle cx={ROUTER.x} cy={ROUTER.y} r={RX} fill="var(--background)" stroke="var(--border-strong)" strokeWidth={1.5} />
            {!reduce && (
              <motion.circle
                key={`flash-${active}`}
                cx={ROUTER.x} cy={ROUTER.y} r={RX} fill="none"
                stroke="var(--foreground)" strokeWidth={2.5}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0, 1, 0] }}
                transition={{ duration: 2.1, times: [0, 0.3, 0.37, 0.6], ease }}
              />
            )}
            <SvgLogoMark cx={ROUTER.x} cy={ROUTER.y} gap={15} r={3.6} color="var(--foreground)" diagColor="var(--muted-2)" />
            <text x={ROUTER.x} y={ROUTER.y + RX + 22} fill="var(--muted)" fontSize={13} textAnchor="middle" className="font-serif italic">
              P/9 Router
            </text>

            {/* model nodes + labels */}
            {MODELS.map((m, i) => {
              const [mx, my] = MPT[i];
              const on = i === pick;
              return (
                <g
                  key={m.id}
                  onClick={() => setActive(i)}
                  className="cursor-pointer"
                  style={{ transition: "opacity .45s ease" }}
                  opacity={on ? 1 : 0.5}
                >
                  {on && <circle cx={mx} cy={my} r={15} fill="var(--accent-soft)" />}
                  <circle
                    cx={mx} cy={my} r={on ? 8 : 6}
                    fill={on ? "var(--foreground)" : "var(--card)"}
                    stroke="var(--border-strong)" strokeWidth={on ? 0 : 1.5}
                    style={{ transition: "all .45s ease" }}
                  />
                  <text x={mx + 22} y={my - 1} className="font-sans">
                    <tspan fill="var(--foreground)" fontSize={15} fontWeight={on ? 600 : 400}>
                      {m.id}
                    </tspan>
                  </text>
                  <text x={mx + 22} y={my + 15} fill="var(--muted-2)" fontSize={12} className="font-sans">
                    {m.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </motion.div>

        {/* ---------- mobile: vertical routing ---------- */}
        <div className="mx-auto mt-10 flex max-w-xs flex-col sm:hidden">
          <div className="flex flex-col items-center text-center">
            <span className="eyebrow">Incoming request</span>
            <AnimatePresence mode="wait">
              <motion.p
                key={active}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                className="mt-1 font-serif text-xl italic"
              >
                {REQUESTS[active].label}
              </motion.p>
            </AnimatePresence>
          </div>

          <VBeam />

          <div className="flex flex-col items-center">
            <span className="grid h-14 w-14 place-items-center rounded-full border border-border-strong bg-background">
              <LogoMark className="h-6 w-6 text-foreground" />
            </span>
            <span className="mt-1.5 font-serif text-sm italic text-muted">
              P/9 Router
            </span>
          </div>

          <VBeam />

          <ul className="space-y-1.5">
            {MODELS.map((m, i) => {
              const on = i === pick;
              return (
                /* Dimmed by colour, not by opacity.
                   This row used to animate to `opacity: 0.4`, which
                   composited the text inside it down to roughly a third of
                   its intended alpha: `text-foreground/80` became an
                   effective 0.32 and failed WCAG contrast on every inactive
                   row. Opacity on a container is invisible to the token
                   system, so no colour choice inside could have fixed it.
                   The muted tokens carry the same hierarchy and pass on
                   their own, and the colour transition still animates. */
                <li
                  key={m.id}
                  onClick={() => setActive(i)}
                  className={cn(
                    "flex cursor-pointer items-center justify-between rounded-lg px-4 py-2.5 transition-colors duration-300",
                    on ? "bg-accent-soft ring-1 ring-border-strong" : "",
                  )}
                >
                  <span className="flex items-center gap-2.5">
                    <LogoMark
                      className={cn("h-4 w-4", on ? "text-foreground" : "text-muted-2")}
                    />
                    <span
                      className={cn(
                        "font-sans text-sm transition-colors duration-300",
                        on ? "font-medium text-foreground" : "text-muted",
                      )}
                    >
                      {m.id}
                    </span>
                  </span>
                  <span className="font-sans text-xs text-muted-2">{m.name}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, ease }}
          className="mx-auto mt-14 grid max-w-4xl gap-x-10 gap-y-8 sm:grid-cols-3"
        >
          {[
            { t: "Per-request routing", d: "A P/9 agent reads each prompt and task, then routes to the best-suited model in the fleet." },
            { t: "Quality preserved", d: "Top-tier answers by sending hard requests to stronger models, easy ones to lighter, cheaper models." },
            { t: "Cost driven down", d: "Most traffic is served by lightweight open-source models, so you only pay for heavyweight compute when it matters." },
          ].map((f) => (
            <div
              key={f.t}
              tabIndex={0}
              className="rounded-lg p-5 outline-none transition-all duration-300 hover:-translate-y-0.5 hover:bg-card hover:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.3)] hover:ring-1 hover:ring-inset hover:ring-border-strong focus:-translate-y-0.5 focus:bg-card focus:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.3)] focus:ring-1 focus:ring-inset focus:ring-border-strong"
            >
              <h3 className="font-serif text-lg text-foreground">{f.t}</h3>
              <p className="mt-2 font-sans text-sm leading-relaxed text-muted">{f.d}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function VBeam() {
  return (
    <div className="relative mx-auto my-4 h-9 w-px">
      <div className="absolute inset-0 border-l border-dashed border-border-strong" />
      <motion.span
        className="absolute left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-foreground"
        initial={{ top: "0%", opacity: 0 }}
        animate={{ top: ["0%", "100%"], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 1.3, ease: "easeInOut", repeat: Infinity, repeatDelay: 0.4 }}
      />
    </div>
  );
}
